import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { Link, createFileRoute, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router'
import { AlertTriangleIcon, ArrowLeftIcon, FileIcon } from 'lucide-react'
import { emailBlocks, emailParagraphs } from '@hedgehog/shared'
import type { OrderState, SupportAction, SupportAttachment, SupportMessage, SupportThreadDetail } from '@hedgehog/shared'
import { opsApi } from '@/components/ops/api'
import { Facts, Muted, Section } from '@/components/ops/section'
import { StateBadge } from '@/components/ops/state-badge'
import { CopyButton } from '@/components/app/copy-button'
import { ErrorState, FormError, LoadingState, errorMessage } from '@/components/app/states'
import { formatAge } from '@/components/app/format'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { Kbd, splitQuoted, typing, useUndo } from '@/components/ops/support'

const parent = getRouteApi('/ops/support')

export const Route = createFileRoute('/ops/support/$threadId')({
  loader: ({ params }) => opsApi<SupportThreadDetail>(`/ops/support/${encodeURIComponent(params.threadId)}`),
  head: ({ loaderData }) => ({ meta: [{ title: `${loaderData?.thread.subject ?? 'Conversation'} · Support · Ops` }] }),
  component: ThreadPage,
  pendingComponent: () => <LoadingState label="Loading conversation" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not open this conversation" />,
})

const STATUS_LABEL = { open: 'Open', waiting: 'Waiting on customer', closed: 'Closed', spam: 'Spam' } as const

const SHORTCUTS: [string, string][] = [
  ['j / k', 'Next / previous conversation'],
  ['o or Enter', 'Open the first conversation'],
  ['Esc', 'Leave the reply box, then back to the list'],
  ['r', 'Write in the reply box'],
  ['d', 'Write a redraft instruction'],
  ['p', 'Switch between write and preview'],
  ['e', 'Close (or reopen) and go to the next'],
  ['!', 'Spam / not spam'],
  ['1 – 4', 'Open, Waiting, Closed, Spam'],
  ['⌘ Enter', 'Send and go to the next'],
  ['⌘ Shift Enter', 'Send and close'],
  ['⌘ Z', 'Undo close or spam'],
  ['?', 'This list'],
]

function ThreadPage() {
  const detail = Route.useLoaderData()
  // A fresh component per conversation resets the draft, errors and pending state.
  return <Thread key={detail.thread.id} detail={detail} />
}

function Thread({ detail }: { detail: SupportThreadDetail }) {
  const { thread, messages, order, otherThreads } = detail
  const { threads } = parent.useLoaderData()
  const { status } = parent.useSearch()
  const navigate = useNavigate()
  const router = useRouter()
  const showUndo = useUndo()
  const replyRef = useRef<HTMLTextAreaElement>(null)
  const instructionRef = useRef<HTMLInputElement>(null)
  const storeKey = `support-draft:${thread.id}`

  const [text, setText] = useState(() => sessionGet(storeKey) ?? thread.draft)
  const [edited, setEdited] = useState(() => sessionGet(storeKey) !== null)
  const [previous, setPrevious] = useState<string | null>(null)
  const [instruction, setInstruction] = useState('')
  const [busy, setBusy] = useState<null | 'send' | 'redraft' | 'status'>(null)
  const [sending, setSending] = useState<string | null>(null)
  const [error, setError] = useState<{ where: 'send' | 'redraft' | 'status'; message: string } | null>(null)
  const [help, setHelp] = useState(false)
  const [view, setView] = useState<'write' | 'preview'>('write')
  const [seenCount] = useState(messages.length)

  // A draft written by the server (new customer message, redraft by someone else) replaces the text unless it was edited here.
  useEffect(() => {
    if (!edited) setText(thread.draft)
  }, [thread.draft, edited])

  const nextId = () => {
    const i = threads.findIndex((t) => t.id === thread.id)
    return (threads[i + 1] ?? threads[i - 1])?.id
  }
  const goNext = (id = nextId()) =>
    id && id !== thread.id ? navigate({ to: '/ops/support/$threadId', params: { threadId: id }, search: { status } }) : navigate({ to: '/ops/support', search: { status } })

  const post = (body: SupportAction) => opsApi<{ draft?: string }>(`/ops/support/${encodeURIComponent(thread.id)}`, { method: 'POST', json: body })

  const updateText = (v: string) => {
    setText(v)
    setEdited(true)
    setPrevious(null)
    sessionSet(storeKey, v)
  }

  const send = async (close: boolean) => {
    const body = text.trim()
    if (body.length < 2 || busy) return
    const next = nextId()
    setBusy('send')
    setError(null)
    setSending(body)
    try {
      await post({ action: 'send', text: body })
      if (close) await post({ action: 'close' })
      sessionDel(storeKey)
      await router.invalidate()
      await goNext(next)
    } catch (e) {
      setError({ where: 'send', message: errorMessage(e) })
    } finally {
      setSending(null)
      setBusy(null)
    }
  }

  const runRedraft = async () => {
    if (busy) return
    setBusy('redraft')
    setError(null)
    try {
      const r = await post({ action: 'redraft', instruction: instruction.trim() || undefined })
      setPrevious(text)
      setText(r.draft ?? '')
      setEdited(false)
      sessionDel(storeKey)
      setInstruction('')
      void router.invalidate()
    } catch (e) {
      setError({ where: 'redraft', message: errorMessage(e) })
    } finally {
      setBusy(null)
    }
  }

  const changeStatus = async (action: 'close' | 'reopen' | 'spam' | 'not_spam') => {
    if (busy) return
    const undoAction = ({ close: 'reopen', reopen: 'close', spam: 'not_spam', not_spam: 'spam' } as const)[action]
    const next = nextId()
    setBusy('status')
    setError(null)
    try {
      await post({ action })
      showUndo({ label: { close: 'Closed', reopen: 'Reopened', spam: 'Marked as spam', not_spam: 'Moved to Open' }[action], run: () => post({ action: undoAction }) })
      await router.invalidate()
      if (action === 'close' || action === 'spam') await goNext(next)
    } catch (e) {
      setError({ where: 'status', message: errorMessage(e) })
    } finally {
      setBusy(null)
    }
  }

  const closed = thread.status === 'closed' || thread.status === 'spam'

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (help) {
        if (e.key === 'Escape') setHelp(false)
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        void send(e.shiftKey)
        return
      }
      if (e.key === 'Escape') {
        const t = e.target as HTMLElement
        if (t.closest('input, textarea')) (t as HTMLElement).blur()
        else navigate({ to: '/ops/support', search: { status } })
        return
      }
      if (typing(e)) return
      if (e.key === 'r') {
        setView('write')
        requestAnimationFrame(() => replyRef.current?.focus())
      } else if (e.key === 'p') setView(view === 'write' ? 'preview' : 'write')
      else if (e.key === 'd') instructionRef.current?.focus()
      else if (e.key === 'e') void changeStatus(closed ? 'reopen' : 'close')
      else if (e.key === '!') void changeStatus(thread.status === 'spam' ? 'not_spam' : 'spam')
      else if (e.key === '?') setHelp(true)
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  return (
    <div className="grid gap-4 xl:h-full xl:min-h-0 xl:grid-cols-[minmax(0,1fr)_18rem]">
      <div className="flex flex-col rounded-lg border border-border xl:min-h-0">
        <header className="flex flex-wrap items-start gap-x-3 gap-y-2 border-b border-border px-4 py-3">
          <Link to="/ops/support" search={{ status }} className="-ml-1 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground xl:hidden" aria-label="Back to the list">
            <ArrowLeftIcon className="size-4" />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-semibold break-words">{thread.subject}</h1>
            <p className="text-muted-foreground">
              {thread.customer} · <Badge variant={thread.status === 'open' ? 'default' : 'secondary'}>{STATUS_LABEL[thread.status]}</Badge>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => void changeStatus(closed ? 'reopen' : 'close')}>
              {closed ? 'Reopen' : 'Close'} <Kbd>e</Kbd>
            </Button>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => void changeStatus(thread.status === 'spam' ? 'not_spam' : 'spam')}>
              {thread.status === 'spam' ? 'Not spam' : 'Spam'} <Kbd>!</Kbd>
            </Button>
            <Button size="sm" variant="ghost" className="hidden xl:inline-flex" aria-label="Keyboard shortcuts" onClick={() => setHelp(true)}>
              <Kbd>?</Kbd>
            </Button>
          </div>
        </header>

        <div className="flex flex-col gap-4 px-4 py-4 xl:min-h-0 xl:flex-1 xl:overflow-y-auto">
          {error?.where === 'status' && <FormError message={error.message} />}
          {thread.escalate && (
            <Alert variant="destructive">
              <AlertTriangleIcon />
              <AlertTitle>Needs a person</AlertTitle>
              <AlertDescription>{thread.escalateReason ?? 'The AI could not answer this from the facts it has.'}</AlertDescription>
            </Alert>
          )}
          {thread.summary && (
            <p>
              <span className="mr-2 text-xs text-muted-foreground">Summary</span>
              {thread.summary}
            </p>
          )}
          <Conversation messages={messages} />
          {sending && (
            <article className="border-l-2 border-primary/40 pl-3 opacity-60">
              <p className="mb-1 text-xs text-muted-foreground">Sending…</p>
              <p className="max-w-[68ch] text-sm leading-6 whitespace-pre-wrap">{sending}</p>
            </article>
          )}
        </div>

        {thread.status !== 'spam' && (
          <div className="sticky bottom-0 flex flex-col gap-2 border-t border-border bg-background px-4 py-3">
            {messages.length > seenCount && edited && (
              <p className="text-sm text-muted-foreground">New message from the customer. Redraft to include it.</p>
            )}
            <div className="flex items-center justify-between gap-2">
              <label htmlFor="reply" className="font-medium">
                Reply <span className="font-normal text-muted-foreground">to {thread.customer}</span>
              </label>
              <span className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{edited ? 'Edited' : text ? 'AI draft' : 'No draft'}</span>
                <span role="group" aria-label="Reply view" className="flex rounded-md border border-border p-0.5 text-xs">
                  {(['write', 'preview'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      aria-pressed={view === v}
                      onClick={() => setView(v)}
                      className={cn('rounded px-2 py-0.5 capitalize text-muted-foreground', view === v && 'bg-muted font-medium text-foreground')}
                    >
                      {v}
                    </button>
                  ))}
                </span>
              </span>
            </div>
            {view === 'preview' ? (
              <EmailPreview subject={/^re:/i.test(thread.subject) ? thread.subject : `Re: ${thread.subject}`} text={text} />
            ) : (
              <Textarea
                id="reply"
                ref={replyRef}
                value={text}
                readOnly={busy === 'send' || busy === 'redraft'}
                onChange={(e) => updateText(e.target.value)}
                placeholder="No draft. Write a reply or redraft below."
                className={cn('field-sizing-content max-h-[50vh] min-h-40 text-sm leading-6', busy === 'redraft' && 'opacity-60')}
              />
            )}
            {previous !== null && (
              <button type="button" className="self-start text-sm underline underline-offset-4" onClick={() => (setText(previous), setPrevious(null))}>
                Undo redraft
              </button>
            )}
            <div className="flex gap-2">
              <Input
                ref={instructionRef}
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                onKeyDown={(e: ReactKeyboardEvent) => e.key === 'Enter' && (e.preventDefault(), void runRedraft())}
                placeholder="Instruction (optional): shorter, offer a call…"
                aria-label="Redraft instruction"
                maxLength={500}
              />
              <Button variant="outline" disabled={!!busy} onClick={() => void runRedraft()}>
                {busy === 'redraft' && <Spinner />} Redraft <Kbd>d</Kbd>
              </Button>
            </div>
            {error?.where === 'redraft' && <FormError message={`Redraft failed: ${error.message}`} />}
            {error?.where === 'send' && (
              <Alert variant="destructive">
                <AlertTitle>Not sent</AlertTitle>
                <AlertDescription>{error.message} Your text is kept.</AlertDescription>
              </Alert>
            )}
            <div className="flex flex-wrap gap-2">
              <Button disabled={!!busy || text.trim().length < 2} onClick={() => void send(false)}>
                {busy === 'send' ? (
                  <>
                    <Spinner /> Sending…
                  </>
                ) : (
                  <>
                    {error?.where === 'send' ? 'Retry send' : 'Send'} <Kbd>⌘↵</Kbd>
                  </>
                )}
              </Button>
              <Button variant="outline" disabled={!!busy || text.trim().length < 2} onClick={() => void send(true)}>
                Send and close <Kbd>⌘⇧↵</Kbd>
              </Button>
            </div>
          </div>
        )}
      </div>

      <aside className="flex flex-col gap-4 xl:overflow-y-auto">
        <Section id="order" title="Order">
          {order ? (
            <Facts
              items={[
                ['Business', <Link to="/ops/orders/$orderId" params={{ orderId: order.id }} className="underline underline-offset-4">{order.businessName}</Link>],
                ['State', <StateBadge state={order.state as OrderState} />],
                ['Domain', order.domain],
                ['Ordered', formatAge(order.createdAt)],
              ]}
            />
          ) : (
            <Muted>No order linked to this email.</Muted>
          )}
        </Section>
        <Section id="customer" title="Customer" actions={<CopyButton value={thread.customer} label="customer email" size="xs" showText={false} />}>
          <p className="break-all">{thread.customer}</p>
          {otherThreads.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1">
              {otherThreads.map((o) => (
                <li key={o.id} className="flex items-center gap-2">
                  <Link to="/ops/support/$threadId" params={{ threadId: o.id }} search={{ status }} className="min-w-0 flex-1 truncate underline underline-offset-4">
                    {o.subject}
                  </Link>
                  <span className="shrink-0 text-xs text-muted-foreground">{STATUS_LABEL[o.status]}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </aside>

      <Dialog open={help} onOpenChange={setHelp}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Keyboard shortcuts</DialogTitle>
          </DialogHeader>
          <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1.5 text-sm">
            {SHORTCUTS.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="font-mono text-muted-foreground">{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** The reply as the customer receives it: paragraphs, links as buttons, the footer. Same rules as the API renderer. */
function EmailPreview({ subject, text }: { subject: string; text: string }) {
  const blocks = emailBlocks(emailParagraphs(text))
  return (
    <div className="max-h-[50vh] min-h-40 overflow-y-auto rounded-md border border-border bg-muted/40 p-3">
      <p className="mb-2 text-xs text-muted-foreground">Subject: {subject}</p>
      <div className="rounded-lg bg-background p-5 text-[15px] leading-relaxed shadow-xs">
        {blocks.length === 0 ? (
          <p className="text-muted-foreground">Nothing to preview yet.</p>
        ) : (
          blocks.map((b, i) =>
            b.kind === 'button' ? (
              <p key={i} className="mb-4">
                <a href={b.url} target="_blank" rel="noreferrer noopener" className="inline-block rounded-lg bg-foreground px-4 py-2.5 text-background no-underline">
                  {b.label}
                </a>
              </p>
            ) : (
              <p key={i} className="mb-4 whitespace-pre-line">
                {b.text}
              </p>
            ),
          )
        )}
        <p className="mt-6 text-[13px] text-muted-foreground">Hedgehog · support@hedgehogsite.com</p>
      </div>
    </div>
  )
}

/** Messages oldest first; with more than three, the older ones fold to one line each. */
function Conversation({ messages }: { messages: SupportMessage[] }) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const foldBefore = messages.length > 3 ? messages.length - 2 : 0
  return (
    <ol className="flex flex-col gap-4">
      {messages.map((m, i) =>
        i < foldBefore && !expanded.has(m.id) ? (
          <li key={m.id}>
            <button
              type="button"
              onClick={() => setExpanded(new Set(expanded).add(m.id))}
              className="flex w-full gap-2 truncate rounded px-1 py-0.5 text-left text-muted-foreground hover:bg-muted"
            >
              <span className="shrink-0 font-medium text-foreground">{m.direction === 'in' ? m.from : 'Hedgehog'}</span>
              <span className="shrink-0">{formatAge(m.createdAt)}</span>
              <span className="truncate">{m.body.slice(0, 100)}</span>
            </button>
          </li>
        ) : (
          <li key={m.id}>
            <MessageView m={m} />
          </li>
        ),
      )}
    </ol>
  )
}

function MessageView({ m }: { m: SupportMessage }) {
  const [showQuoted, setShowQuoted] = useState(false)
  const { body, quoted } = splitQuoted(m.body)
  return (
    <article className={cn(m.direction === 'out' && 'border-l-2 border-primary/40 pl-3')}>
      <p className="mb-1 flex flex-wrap gap-x-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{m.direction === 'in' ? m.from : `Sent by ${m.sentBy?.replace(/^ops:/, '') ?? 'Hedgehog'}`}</span>
        <time dateTime={new Date(m.createdAt).toISOString()} title={new Date(m.createdAt).toLocaleString()}>
          {formatAge(m.createdAt)}
        </time>
      </p>
      <p className="max-w-[68ch] text-sm leading-6 break-words whitespace-pre-wrap">{body}</p>
      {quoted && (
        <>
          <button type="button" className="mt-1 text-xs text-muted-foreground underline underline-offset-4" onClick={() => setShowQuoted(!showQuoted)}>
            {showQuoted ? 'Hide quoted text' : 'Show quoted text'}
          </button>
          {showQuoted && <p className="mt-1 max-w-[68ch] text-xs leading-5 whitespace-pre-wrap text-muted-foreground">{quoted}</p>}
        </>
      )}
      {m.attachments.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {m.attachments.map((a) => (
            <li key={a.url}>
              <AttachmentChip a={a} />
            </li>
          ))}
        </ul>
      )}
    </article>
  )
}

function AttachmentChip({ a }: { a: SupportAttachment }) {
  const [broken, setBroken] = useState(false)
  return (
    <a href={a.url} target="_blank" rel="noreferrer noopener" className="flex items-center gap-2 rounded-md border border-border p-1.5 pr-3 hover:bg-muted">
      {a.type.startsWith('image/') && a.size > 0 && !broken ? (
        <img src={a.url} alt="" className="size-10 rounded object-cover" loading="lazy" onError={() => setBroken(true)} />
      ) : (
        <FileIcon className="size-4 text-muted-foreground" aria-hidden />
      )}
      <span className="max-w-48 truncate">{a.name}</span>
      <span className="text-xs text-muted-foreground">{fileSize(a.size)}</span>
    </a>
  )
}

function fileSize(n: number): string {
  return n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`
}

function sessionGet(k: string): string | null {
  try {
    return sessionStorage.getItem(k)
  } catch {
    return null
  }
}
function sessionSet(k: string, v: string) {
  try {
    sessionStorage.setItem(k, v)
  } catch {
    // Private mode: the draft is kept only while the page is open.
  }
}
function sessionDel(k: string) {
  try {
    sessionStorage.removeItem(k)
  } catch {
    // ignore
  }
}
